import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useGlobalMetrics } from '@/hooks/useGlobalMetrics';
import { useAnimatedCounter } from '@/hooks/useAnimatedCounter';
import { Trophy, Target, Zap, ArrowRight, Shield } from 'lucide-react';

export function HeroVideoSection() {
  const navigate = useNavigate();
  const { data: metrics, isLoading } = useGlobalMetrics();

  const sessionsCounter = useAnimatedCounter({ 
    end: metrics?.totalSessions || 0, 
    duration: 2000,
    delay: 800 
  });

  const touchesCounter = useAnimatedCounter({ 
    end: metrics?.totalTouches || 0, 
    duration: 2000,
    delay: 900 
  });

  const skillsCounter = useAnimatedCounter({ 
    end: 62, 
    duration: 2000,
    delay: 1000 
  });

  const scrollToSignIn = () => {
    document.getElementById('sign-in')?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <section className="relative min-h-[85vh] h-[calc(100dvh-3.5rem)] max-h-[800px] w-full overflow-hidden md:min-h-[600px] md:max-h-[900px]">
      {/* Video Container */}
      <div className="absolute inset-0 overflow-hidden">
        <iframe 
          src="https://player.vimeo.com/video/1137868082?badge=0&autopause=0&autoplay=1&muted=1&loop=1&background=1&player_id=0&app_id=58479"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full"
          style={{ minWidth: '177.77vh', minHeight: '56.25vw' }}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
          title="FutWork — Train the Feet. Sharpen the Mind."
          loading="lazy"
        />
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/80" />

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 text-center">
        <img 
          src="https://lztlmniinqehhxbqftsn.supabase.co/storage/v1/object/public/badge-icons/Header%20Logo_90-01.png" 
          alt="FutWork Icon" 
          className="h-24 md:h-36 mb-4 md:mb-6 animate-fade-in drop-shadow-2xl"
        />
        
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-4 animate-fade-in tracking-tight leading-tight max-w-3xl">
          Train the Feet. Sharpen the Mind.
        </h1>
        
        <p className="text-sm md:text-lg text-white/85 mb-4 md:mb-5 max-w-xl animate-fade-in font-medium px-2 leading-relaxed" style={{ animationDelay: '300ms' }}>
          The first AI-powered training lab for elite youth soccer. Physical ball mastery meets tactical decision-making.
        </p>

        {/* Trust Badge */}
        <div className="flex items-center gap-2 mb-5 md:mb-7 animate-fade-in" style={{ animationDelay: '450ms' }}>
          <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-md rounded-full px-3 py-1.5 border border-white/20">
            <Shield className="h-3.5 w-3.5 text-amber-400" />
            <span className="text-[11px] md:text-xs text-white/80 font-medium">
              Built by Operators · Trusted by Clubs
            </span>
          </div>
        </div>

        {/* Compact Metrics Row */}
        <div className="flex items-center justify-center gap-3 md:gap-5 mb-6 md:mb-8 animate-fade-in" style={{ animationDelay: '600ms' }}>
          <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-2.5 border border-white/20">
            <div className="flex items-center gap-1.5">
              <Trophy className="h-4 w-4 text-amber-400" />
              <span className="text-lg md:text-2xl font-bold text-white tabular-nums">
                {isLoading ? '...' : sessionsCounter.value.toLocaleString()}
              </span>
            </div>
            <span className="text-[10px] md:text-xs text-white/60">Sessions</span>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-2.5 border border-white/20">
            <div className="flex items-center gap-1.5">
              <Target className="h-4 w-4 text-amber-400" />
              <span className="text-lg md:text-2xl font-bold text-white tabular-nums">
                {isLoading ? '...' : touchesCounter.value.toLocaleString()}
              </span>
            </div>
            <span className="text-[10px] md:text-xs text-white/60">Touches</span>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-2.5 border border-white/20">
            <div className="flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-amber-400" />
              <span className="text-lg md:text-2xl font-bold text-white tabular-nums">
                {skillsCounter.value}
              </span>
            </div>
            <span className="text-[10px] md:text-xs text-white/60">Skills</span>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col w-full max-w-xs sm:flex-row sm:max-w-none sm:w-auto gap-2.5 animate-fade-in px-2" style={{ animationDelay: '900ms' }}>
          <Button
            size="lg"
            onClick={() => navigate('/find-club')}
            className="w-full sm:w-auto bg-gradient-to-r from-amber-400 to-orange-500 text-foreground hover:from-amber-500 hover:to-orange-600 rounded-full px-6 h-12 md:h-14 text-sm md:text-base font-semibold shadow-lg shadow-amber-500/25 active:scale-[0.98]"
          >
            Start Training
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={scrollToSignIn}
            className="w-full sm:w-auto bg-white/10 backdrop-blur-md text-white border-white/30 hover:bg-white/20 rounded-full px-6 h-12 md:h-14 text-sm md:text-base font-medium active:scale-[0.98]"
          >
            Sign In
          </Button>
        </div>
      </div>
    </section>
  );
}
